import { z } from 'zod';

import { oneOrMany } from '../../utils.js';
import {
  BaseFlowBlockSchema,
  BlockType,
  DurationSchemaWithMax,
  DurationUnits,
  ProducedType,
  returnTypeSchema,
} from '../base/baseSchema.js';

export enum HieDocumentFormat {
  ConsolidatedData = 'ConsolidatedData',
  ConsolidatedDataPdf = 'ConsolidatedDataPdf',
  ConsolidatedDataHtml = 'ConsolidatedDataHtml',
  BulkDownloadUrls = 'BulkDownloadUrls',
}

export enum FhirResourceType {
  All = 'All',
  AllergyIntolerance = 'AllergyIntolerance',
  Communication = 'Communication',
  Composition = 'Composition',
  Condition = 'Condition',
  Consent = 'Consent',
  Coverage = 'Coverage',
  Device = 'Device',
  DiagnosticReport = 'DiagnosticReport',
  DocumentReference = 'DocumentReference',
  Encounter = 'Encounter',
  FamilyMemberHistory = 'FamilyMemberHistory',
  Goal = 'Goal',
  Immunization = 'Immunization',
  Medication = 'Medication',
  Observation = 'Observation',
  Practitioner = 'Practitioner',
  Procedure = 'Procedure',
  Provenance = 'Provenance',
  RelatedPerson = 'RelatedPerson',
  ServiceRequest = 'ServiceRequest',
}

export const BaseHieGetDocumentsBlockConfigSchema = z.object({
  sourceBlock: z.object({
    sourceBlockName: z.string(),
  }),
  timeout: DurationSchemaWithMax('timeout', {
    value: 30,
    units: DurationUnits.Minutes,
  }),
});

export const BaseConsolidatedBlockConfigSchema = BaseHieGetDocumentsBlockConfigSchema.extend({
  resources: oneOrMany(z.nativeEnum(FhirResourceType)).default([]),
  dateTo: z.string().optional(),
  dateFrom: z.string().optional(),
});

export const ConsolidatedJsonConfigSchema = BaseConsolidatedBlockConfigSchema.extend({
  documentFormat: z.enum([HieDocumentFormat.ConsolidatedData]),
  includeRelatedRecords: z.boolean().default(false),
});

export type ConsolidatedJsonConfig = z.infer<typeof ConsolidatedJsonConfigSchema>;

export const ConsolidatedRenderedConfigSchema = BaseConsolidatedBlockConfigSchema.extend({
  documentFormat: z.enum([
    HieDocumentFormat.ConsolidatedDataPdf,
    HieDocumentFormat.ConsolidatedDataHtml,
  ]),
});

export const BulkDownloadUrlsConfigSchema = BaseHieGetDocumentsBlockConfigSchema.extend({
  documentFormat: z.literal(HieDocumentFormat.BulkDownloadUrls),
});

export const HieGetDocumentsBlockConfigSchema = z.discriminatedUnion('documentFormat', [
  ConsolidatedJsonConfigSchema,
  ConsolidatedRenderedConfigSchema,
  BulkDownloadUrlsConfigSchema,
]);

export type HieGetDocumentsBlockConfig = z.infer<typeof HieGetDocumentsBlockConfigSchema>;

export const HieGetDocumentsBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.HieGetDocuments),
  config: HieGetDocumentsBlockConfigSchema,
  returnType: returnTypeSchema(ProducedType.Any),
});

export type HieGetDocumentsBlock = z.infer<typeof HieGetDocumentsBlockSchema>;
